// Pure pediatric dose calculation — no React, no path aliases, so it can be unit-tested directly.
import type { PedsDosing } from './api';
import type { ParsedDosage } from './dosage';

// ── Types ────────────────────────────────────────────────────────────────────

// error   = red: likely harm, must be acknowledged before the dose is used
// warning = amber: worth a look, one line
// info    = only shown under "Show working"
export type NoteLevel = 'error' | 'warning' | 'info';
export type Note = { text: string; level: NoteLevel };
export type Family = 'mass' | 'IU' | 'mEq';
export type DoseMode = 'perKg' | 'fixed';
export type Basis = 'day' | 'dose';
export type ProductKind = 'liquid' | 'solid' | 'other';
export type FrequencyKey = 'OD' | 'BD' | 'TDS' | 'QID' | 'Q4H' | 'SOS';
export type RowMatch = { weight: boolean | null; age: boolean | null; inRange: boolean };

export type Strength = {
  amount: number | null;         // e.g. 250
  unit: string;                  // e.g. 'mg'
  perVolume: number | null;      // e.g. 5 (liquids only)
  perVolumeUnit: string;         // e.g. 'ml'
};

export type Product = {
  form: string;
  kind: ProductKind;
  splittable: boolean;           // false for capsules, sachets, SR/EC tablets
  strength: Strength;
};

export type CoIngredientInput = { name: string; amount: number; unit: string; perVolume: number | null };
export type CoIngredient = { name: string; perDose: number; family: Family };

export type Give = {
  value: number;                 // what to give, snapped to a measurable step
  exact: number;                 // unrounded
  unit: string;                  // 'ml' | 'tablet(s)' …
  display: string;               // "3.5 ml", "½ tablet"
  patternValue: string;          // "3.5ml", "½"
};

export const DOSE_UNITS = ['mg', 'mcg', 'IU', 'mEq'] as const;
export const STRENGTH_UNITS = ['mg', 'mcg', 'g', 'IU', 'mEq'] as const;

export const FREQUENCIES: { key: FrequencyKey; label: string; perDay: number }[] = [
  { key: 'OD',  label: 'OD',       perDay: 1 },
  { key: 'BD',  label: 'BD',       perDay: 2 },
  { key: 'TDS', label: 'TDS',      perDay: 3 },
  { key: 'QID', label: 'QID',      perDay: 4 },
  { key: 'Q4H', label: '4-hourly', perDay: 6 },
  // SOS: daily totals assume the maximum number of doses in 24 h
  { key: 'SOS', label: 'SOS',      perDay: 4 },
];
export const SOS_MIN_GAP_HOURS = 6;

const TO_MG: Record<string, number> = { mg: 1, mcg: 0.001, g: 1000 };
const EPS = 1e-6;
const LIQUID_FORM_KEYWORDS = ['syrup', 'suspension', 'solution', 'drops', 'elixir', 'linctus', 'mixture', 'liquid', 'emulsion', 'injection', 'infusion'];
const SOLID_FORM_KEYWORDS = ['tablet', 'tab', 'capsule', 'sachet', 'granule', 'powder'];
const PARENTERAL_ROUTES = ['injection', 'iv', 'im', 'sc', 'parenteral', 'intravenous', 'intramuscular', 'subcutaneous'];

// Approximate weight-for-age bands (WHO-based up to 10 y); used only as a hint
const WEIGHT_FOR_AGE: { months: number; kg: [number, number] }[] = [
  { months: 0, kg: [3, 4] }, { months: 3, kg: [5, 7] }, { months: 6, kg: [6, 9] }, { months: 12, kg: [8, 12] },
  { months: 24, kg: [10, 14] }, { months: 48, kg: [14, 18] }, { months: 72, kg: [18, 24] },
  { months: 96, kg: [22, 30] }, { months: 120, kg: [28, 38] }, { months: 144, kg: [35, 50] },
];

// ── Formatting / units ───────────────────────────────────────────────────────

export function formatNumber(n: number): string {
  // Max 2 dp, no trailing zeros ("2.4" not "2.40") so displayed precision isn't overstated
  return String(Number(n.toFixed(2)));
}

export function formatMass(mg: number): string {
  if (mg > 0 && mg < 1) return `${formatNumber(mg * 1000)} mcg`;
  return `${formatNumber(mg)} mg`;
}

export function normUnit(u: string | null | undefined): string {
  const v = (u ?? '').trim();
  const l = v.toLowerCase();
  if (l === 'µg' || l === 'ug' || l === 'mcg') return 'mcg';
  if (l === 'iu') return 'IU';
  if (l === 'meq') return 'mEq';
  return l;
}

export function unitFamily(u: string | null | undefined): Family | null {
  const n = normUnit(u);
  if (n in TO_MG) return 'mass';
  if (n === 'IU') return 'IU';
  if (n === 'mEq') return 'mEq';
  return null;
}

// Base unit per family: mg for mass, the unit itself otherwise
export function toBase(v: number, u: string): number {
  const n = normUnit(u);
  return n in TO_MG ? v * TO_MG[n] : v;
}

export function formatAmount(base: number, family: Family): string {
  return family === 'mass' ? formatMass(base) : `${formatNumber(base)} ${family}`;
}

export function formatAge(months: number): string {
  return months < 12 ? `${months}m` : months % 12 === 0 ? `${months / 12}y` : `${Math.floor(months / 12)}y${months % 12}m`;
}

// ½-0-½ style used by the prescription dosage options
export function formatFraction(v: number): string {
  const whole = Math.floor(v + EPS);
  const q = Math.round((v - whole) * 4);
  const frac = ['', '¼', '½', '¾'][q] ?? '';
  if (q === 4) return String(whole + 1);
  return whole === 0 ? (frac || '0') : `${whole}${frac}`;
}

export function typicalWeightForAge(months: number): string | null {
  if (months < 0 || months > 180) return null;
  const band = [...WEIGHT_FOR_AGE].reverse().find(b => b.months <= months);
  return band ? `${band.kg[0]}–${band.kg[1]} kg` : null;
}

// ── Product helpers ──────────────────────────────────────────────────────────

export function isLiquidForm(form: string): boolean {
  const f = form.toLowerCase();
  return LIQUID_FORM_KEYWORDS.some(k => f.includes(k));
}

export function isDropsForm(form: string): boolean {
  return form.toLowerCase().includes('drop');
}

// Declared form wins over the DB's strength_type: syrups are often stored as "solid" when the
// "/5 ml" part of the strength is missing.
export function productKind(form: string, strengthType: string | null | undefined): ProductKind {
  if (isLiquidForm(form)) return 'liquid';
  const f = form.toLowerCase();
  if (SOLID_FORM_KEYWORDS.some(k => f.includes(k))) return 'solid';
  if (strengthType === 'liquid') return 'liquid';
  if (strengthType === 'solid') return 'solid';
  return 'other';
}

export function solidUnitLabel(form: string): string {
  const f = form.toLowerCase();
  if (f.includes('capsule')) return 'capsule';
  if (f.includes('sachet'))  return 'sachet';
  return 'tablet';
}

export function isSplittableForm(form: string): boolean {
  const f = form.toLowerCase();
  return !f.includes('capsule') && !f.includes('sachet');
}

// Peds dosing rows are route-specific (seed data is oral); an injectable must not inherit oral doses or caps.
export function routeMatches(rowRoute: string | null | undefined, drugRoute: string | null | undefined): boolean {
  const norm = (r: string | null | undefined) => {
    const v = (r || 'oral').toLowerCase();
    return PARENTERAL_ROUTES.includes(v) ? 'injection' : v;
  };
  return norm(rowRoute) === norm(drugRoute);
}

// ── Reference rows ───────────────────────────────────────────────────────────

// Tri-state match: null = not known yet (no weight/age entered) — never reported as "out of range".
export function matchRow(row: PedsDosing, weightKg: number | null, ageMonths: number | null): RowMatch {
  const inBounds = (v: number, min: number | null, max: number | null) =>
    (min == null || Number(min) <= v + EPS) && (max == null || Number(max) >= v - EPS);
  const weight = weightKg == null ? null : inBounds(weightKg, row.min_weight_kg, row.max_weight_kg);
  const age    = ageMonths == null ? null : inBounds(ageMonths, row.min_age_months, row.max_age_months);
  return { weight, age, inRange: weight !== false && age !== false };
}

export function rowMode(row: PedsDosing): DoseMode {
  return row.dose_type === 'fixed' ? 'fixed' : 'perKg';
}

// Doctors mostly use the top of a range; a curated usual value overrides that
export function rowDefaultRate(row: PedsDosing): number {
  return Number(row.usual_dose_per_kg ?? row.dose_per_kg_max ?? row.dose_per_kg);
}

export function rowRangeLabel(row: PedsDosing): string {
  const lo = formatNumber(Number(row.dose_per_kg));
  const hi = row.dose_per_kg_max != null ? formatNumber(Number(row.dose_per_kg_max)) : null;
  const perKg = rowMode(row) === 'perKg' ? '/kg' : '';
  return `${hi ? `${lo}–${hi}` : lo} ${normUnit(row.dose_unit)}${perKg}/${row.dose_basis === 'per_dose' ? 'dose' : 'day'}`;
}

export function rowBoundsLabel(row: PedsDosing): string {
  const parts: string[] = [];
  if (row.min_weight_kg != null) parts.push(`≥${Number(row.min_weight_kg)} kg`);
  if (row.max_weight_kg != null) parts.push(`≤${Number(row.max_weight_kg)} kg`);
  if (row.min_age_months != null) parts.push(`≥${formatAge(Number(row.min_age_months))}`);
  if (row.max_age_months != null) parts.push(`≤${formatAge(Number(row.max_age_months))}`);
  return parts.join(', ');
}

// ── Pattern ──────────────────────────────────────────────────────────────────

export function dosagePattern(v: string, freq: FrequencyKey): string {
  switch (freq) {
    case 'OD':  return `${v}-0-0`;
    case 'BD':  return `${v}-0-${v}`;
    case 'TDS': return `${v}-${v}-${v}`;
    case 'QID': return `${v}-${v}-${v}-${v}`;
    case 'Q4H': return `${v} every 4 hrs`;
    case 'SOS': return `${v} SOS`;
  }
}

// ── Calculation ──────────────────────────────────────────────────────────────

export type DoseInput = {
  weightKg: number;              // NaN / ≤0 when not entered
  ageMonths: number | null;
  mode: DoseMode;
  rate: number;                  // per kg (perKg mode) or absolute amount (fixed mode)
  rateUnit: string;
  basis: Basis;
  frequency: FrequencyKey;
  product: Product | null;       // null = no drug and no manual strength
  moleculeName: string | null;
  coIngredients: CoIngredientInput[];
  moleculeAutoSelected: boolean;
  rows: PedsDosing[];            // route-filtered rows for the selected molecule
  appliedRowId: number | null;
  // Checking a dose the doctor already typed: override the doses/day implied by `frequency`,
  // and report amounts over a maximum as errors instead of capping them.
  dosesPerDay?: number;
  checkOnly?: boolean;
};

export type DoseResult = {
  validInputs: boolean;
  family: Family | null;
  perDose: number | null;        // base units (mg for mass), after caps
  daily: number | null;
  rawPerDose: number | null;     // before caps
  rawDaily: number | null;
  capped: boolean;
  referenceRow: PedsDosing | null;
  give: Give | null;
  pattern: string | null;
  delivered: { perDose: number; perKgPerDose: number | null; perKgPerDay: number | null } | null;
  needs: 'strength' | 'perVolume' | null;   // which strength field must be filled to get ml / tablets
  coIngredients: CoIngredient[];
  notes: Note[];
};

export function computeDose(input: DoseInput): DoseResult {
  const { weightKg, ageMonths, mode, rate, basis, frequency, product, moleculeAutoSelected, appliedRowId } = input;
  const rateUnit = normUnit(input.rateUnit);
  const notes: Note[] = [];
  const add = (level: NoteLevel, text: string) => notes.push({ level, text });
  const hasWeight = weightKg > 0;
  const perDay = input.dosesPerDay ?? FREQUENCIES.find(f => f.key === frequency)!.perDay;
  const family = unitFamily(rateUnit);
  const name = input.moleculeName ?? 'this drug';

  // Suggestions for an unconfirmed ingredient must never influence the result
  const rows = moleculeAutoSelected ? [] : input.rows;
  const matches = new Map(rows.map(r => [r.id, matchRow(r, hasWeight ? weightKg : null, ageMonths)]));
  const rowsInRange = rows.filter(r => matches.get(r.id)!.inRange);
  const appliedRow = rows.find(r => r.id === appliedRowId) ?? null;
  // The doctor's explicit choice wins; otherwise only an unambiguous single match is used
  const referenceRow = appliedRow ?? (rowsInRange.length === 1 ? rowsInRange[0] : null);

  if (moleculeAutoSelected) add('warning', `Confirm the ingredient — ${name} was picked automatically`);

  // ── Patient sanity ────────────────────────────────────────────────────────
  if (hasWeight) {
    if (weightKg >= 500)      add('error', `${weightKg} kg looks like grams — re-enter in kg`);
    else if (weightKg > 120)  add('error', `${weightKg} kg is implausible for a child — pounds? (${formatNumber(weightKg * 0.4536)} kg)`);
    else if (weightKg > 40)   add('warning', 'Over 40 kg — check whether the adult dose applies');
    else if (weightKg < 2)    add('warning', `${weightKg} kg — confirm kg (not grams); use neonatal guidance`);
  }
  if (ageMonths != null) {
    if (ageMonths < 1)        add('warning', 'Newborn — use neonatal dosing guidance');
    else if (ageMonths > 216) add('warning', 'Over 18 years — use adult dosing');
  }
  const ageBounded = rows.find(r => r.min_age_months != null || r.max_age_months != null);
  if (ageMonths == null && ageBounded) add('warning', `Add age — ${name} has age limits (${rowBoundsLabel(ageBounded)})`);
  if (referenceRow) {
    const m = matches.get(referenceRow.id)!;
    if (m.age === false)         add('error', `Not recommended at this age (${rowBoundsLabel(referenceRow)})`);
    else if (m.weight === false) add('warning', `Standard dose is for ${rowBoundsLabel(referenceRow)} — outside this weight`);
  } else if (rows.length > 0 && ageMonths != null && rows.every(r => matches.get(r.id)!.age === false)) {
    add('error', `Not recommended at this age (${rows.map(rowBoundsLabel).filter(Boolean).join('; ')})`);
  }

  const validInputs = rate > 0 && family !== null && (mode === 'fixed' || hasWeight);
  const empty: DoseResult = { validInputs, family, perDose: null, daily: null, rawPerDose: null, rawDaily: null, capped: false,
    referenceRow, give: null, pattern: null, delivered: null, needs: null, coIngredients: [], notes };
  if (!validInputs) return empty;

  // ── Dose ──────────────────────────────────────────────────────────────────
  const amount = toBase(rate, rateUnit);   // per kg or absolute, in base units
  const scale = mode === 'perKg' ? weightKg : 1;
  let perDose = (basis === 'dose' ? amount : amount / perDay) * scale;
  let daily   = (basis === 'day'  ? amount : amount * perDay) * scale;
  const rawPerDose = perDose;
  const rawDaily   = daily;
  const fmt = (v: number) => formatAmount(v, family!);

  // ── Unit sanity ───────────────────────────────────────────────────────────
  const rowUnits = [...new Set(rows.map(r => normUnit(r.dose_unit)))];
  const strengthUnit = product?.strength.amount ? normUnit(product.strength.unit) : null;
  if (rowUnits.length === 1 && rowUnits[0] !== rateUnit) {
    const ru = rowUnits[0];
    if (unitFamily(ru) === 'mass' && family === 'mass')
      add('error', `Unit mix-up? Standard dose is in ${ru}, you entered ${rateUnit} — 1000× ${TO_MG[rateUnit] > TO_MG[ru] ? 'higher' : 'lower'}`);
    else
      add('error', `Standard dose is in ${ru}, you entered ${rateUnit}`);
  } else if (rowUnits.length === 0 && strengthUnit && family === 'mass') {
    if (rateUnit === 'mg' && strengthUnit === 'mcg')
      add('error', `Unit mix-up? Strength is in mcg, dose in mg — 1000× higher`);
    else if (rateUnit === 'mcg' && strengthUnit === 'mg')
      add('warning', `Dose in mcg, strength in mg — fine for mcg-dosed drugs, else 1000× low`);
  }

  // ── Recommended range ─────────────────────────────────────────────────────
  // Only rows with the same mode (per-kg vs fixed) and unit family can be compared
  const comparable = (r: PedsDosing) => rowMode(r) === mode && unitFamily(r.dose_unit) === family;
  const perKgPerDose = mode === 'perKg' ? perDose / weightKg : perDose;
  const perKgPerDay  = mode === 'perKg' ? daily / weightKg : daily;
  const rowRange = (r: PedsDosing) => {
    const lo = toBase(Number(r.dose_per_kg), normUnit(r.dose_unit));
    const hi = toBase(Number(r.dose_per_kg_max ?? r.dose_per_kg), normUnit(r.dose_unit));
    const v = r.dose_basis === 'per_dose' ? perKgPerDose : perKgPerDay;
    return { lo, hi, v, pos: v > hi * (1 + EPS) ? 'above' : v < lo * (1 - EPS) ? 'below' : 'within' };
  };
  const enteredLabel = `${formatNumber(rate)} ${rateUnit}${mode === 'perKg' ? '/kg' : ''}/${basis}`;
  let rangeCeilingPerDose: number | null = null;   // top of the recommended range as an absolute per-dose amount
  if (referenceRow && comparable(referenceRow)) {
    const { hi, v, pos } = rowRange(referenceRow);
    const src = referenceRow.source;
    if (pos === 'above') {
      const ratio = v / hi;
      add('error', ratio >= 1.5
        ? `${enteredLabel} is ${formatNumber(Math.round(ratio * 10) / 10)}× the ${src} max (${rowRangeLabel(referenceRow)})`
        : `${enteredLabel} is above the ${src} range (${rowRangeLabel(referenceRow)})`);
    } else {
      if (pos === 'below') add('warning', `Below the ${src} range (${rowRangeLabel(referenceRow)}) — may be too weak`);
      const hiPerDose = referenceRow.dose_basis === 'per_dose' ? hi : hi / perDay;
      rangeCeilingPerDose = hiPerDose * scale;
    }
  } else if (!referenceRow) {
    const comparableInRange = rowsInRange.filter(comparable);
    if (comparableInRange.length > 1) {
      const positions = comparableInRange.map(r => rowRange(r).pos);
      if (positions.every(p => p === 'above'))
        add('error', `${enteredLabel} is above every listed range (${comparableInRange.map(rowRangeLabel).join('; ')})`);
      else if (positions.every(p => p === 'below'))
        add('warning', 'Below every listed range — may be too weak');
    }
  }

  // ── Max-dose caps ─────────────────────────────────────────────────────────
  // Specific row → its limits. Several plausible indications → the most permissive limit, so a
  // legitimate high-dose regimen is never capped by another indication's limit.
  const capRows = referenceRow ? [referenceRow] : rowsInRange;
  const capOk = (r: PedsDosing) => unitFamily(r.dose_unit) === family;   // caps are in the row's unit family (mg for mass)
  let maxSingle: number | null = null;
  let maxDaily: number | null = null;
  if (capRows.length && capRows.every(capOk)) {
    const permissive = (vals: (number | null)[]) => vals.some(v => v == null) ? null : Math.max(...(vals as number[]));
    maxSingle = permissive(capRows.map(r => r.max_single_dose != null ? Number(r.max_single_dose) : null));
    maxDaily  = permissive(capRows.map(r => r.max_daily_dose  != null ? Number(r.max_daily_dose)  : null));
  }
  let capped = false;
  if (input.checkOnly) {
    if (maxSingle !== null && perDose > maxSingle + EPS) add('error', `${fmt(perDose)}/dose is over the max ${fmt(maxSingle)}/dose`);
    if (maxDaily !== null && daily > maxDaily + EPS)     add('error', `${fmt(daily)}/day is over the max ${fmt(maxDaily)}/day`);
    maxSingle = null;
    maxDaily = null;
  }
  if (maxSingle !== null && perDose > maxSingle + EPS) {
    add('warning', `Capped at max ${fmt(maxSingle)}/dose`);
    perDose = maxSingle;
    daily = maxSingle * perDay;
    capped = true;
  }
  // Daily cap is checked independently — even after a single-dose cap, daily may still exceed
  if (maxDaily !== null && daily > maxDaily + EPS) {
    add('warning', `Capped at max ${fmt(maxDaily)}/day`);
    daily = maxDaily;
    perDose = maxDaily / perDay;
    capped = true;
  }
  if (!capped && family === 'mass' && daily > 3000)
    add('warning', `${fmt(daily)}/day is high — check kg vs lb and per day vs per dose`);

  // Never round the amount given up past a limit
  const ceilings = [maxSingle, maxDaily != null ? maxDaily / perDay : null, rangeCeilingPerDose].filter((v): v is number => v != null);
  const ceiling = ceilings.length ? Math.min(...ceilings) : null;

  // ── Formulation ───────────────────────────────────────────────────────────
  let give: Give | null = null;
  let needs: DoseResult['needs'] = null;
  const coIngredients: CoIngredient[] = [];
  const s = product?.strength;

  if (product && s) {
    const sFamily = unitFamily(s.unit);
    if (!s.amount || s.amount <= 0 || !sFamily) {
      needs = 'strength';
    } else if (sFamily !== family) {
      add('error', `Dose is in ${rateUnit} but strength is in ${normUnit(s.unit)}`);
    } else if (product.kind === 'liquid' && !(s.perVolume && s.perVolume > 0)) {
      needs = 'perVolume';
    } else if (product.kind === 'other') {
      add('info', 'ml / tablet count is not available for this form');
    } else {
      const strengthBase = toBase(s.amount, s.unit);
      // amount of active ingredient in one "unit" of product (1 ml or 1 tablet)
      const perUnit = product.kind === 'liquid' ? strengthBase / s.perVolume! : strengthBase;
      const exact = perDose / perUnit;
      const fitsCeiling = (v: number) => ceiling == null || v * perUnit <= ceiling * (1 + 1e-3);

      if (product.kind === 'liquid') {
        if (exact < 0.1) {
          add('error', `Under 0.1 ml (${formatNumber(exact)} ml) — too small to measure; check dose and unit`);
        } else {
          // 0.1 ml steps under 2 ml (1–2 ml syringes), 0.5 ml steps above
          const step = exact < 2 ? 0.1 : 0.5;
          let value = Math.round(exact / step) * step;
          if (!fitsCeiling(value)) value = Math.floor(exact / step + EPS) * step;
          value = Number(value.toFixed(2));
          const v = formatNumber(value);
          give = { value, exact, unit: s.perVolumeUnit || 'ml', display: `${v} ${s.perVolumeUnit || 'ml'}`, patternValue: `${v}${s.perVolumeUnit || 'ml'}` };
          if (value > 20) add('warning', `${v} ml per dose is a large volume — check per day vs per dose`);
          if (value < 0.5) add('info', 'Under 0.5 ml — measure with a 1 ml oral syringe');
        }
      } else {
        const unitName = solidUnitLabel(product.form);
        let value = product.splittable ? Math.round(exact * 4) / 4 : Math.round(exact);
        if (!fitsCeiling(value)) value = product.splittable ? Math.floor(exact * 4 + EPS) / 4 : Math.floor(exact + EPS);
        // Rounding a whole unit up by more than 25% (e.g. ½ capsule → 1) is an overdose, not a rounding
        if (value <= 0 || (value - exact) / exact > 0.25) {
          add('error', product.splittable
            ? `Less than ¼ ${unitName} — use a liquid form`
            : `Less than 1 ${unitName} and it can't be split — use a liquid form`);
        } else {
          const frac = formatFraction(value);
          give = { value, exact, unit: `${unitName}${value > 1 ? 's' : ''}`, display: `${frac} ${unitName}${value > 1 ? 's' : ''}`, patternValue: frac };
          if (value > 4) add('warning', `${frac} ${unitName}s per dose — consider a higher strength`);
        }
      }

      if (give) {
        const deliveredPerDose = give.value * perUnit;
        const deviation = (deliveredPerDose - perDose) / perDose;
        if (Math.abs(deviation) > 0.1)
          add('warning', `Nearest measurable dose is ${Math.round(deviation * 100) > 0 ? '+' : ''}${Math.round(deviation * 100)}% off — consider another strength or form`);

        // Combination products: the other ingredients come along in fixed proportion
        for (const c of input.coIngredients) {
          const cFamily = unitFamily(c.unit);
          if (!cFamily || !c.amount) continue;
          const cBase = toBase(c.amount, c.unit);
          const cPerDose = product.kind === 'liquid'
            ? (give.value / (c.perVolume ?? s.perVolume!)) * cBase
            : give.value * cBase;
          coIngredients.push({ name: c.name, perDose: cPerDose, family: cFamily });
        }
      }
    }
  }

  const deliveredPerDose = give && s?.amount
    ? give.value * (product!.kind === 'liquid' ? toBase(s.amount, s.unit) / s.perVolume! : toBase(s.amount, s.unit))
    : perDose;
  const delivered = {
    perDose: deliveredPerDose,
    perKgPerDose: hasWeight ? deliveredPerDose / weightKg : null,
    perKgPerDay:  hasWeight ? (deliveredPerDose * perDay) / weightKg : null,
  };

  return {
    validInputs, family, perDose, daily, rawPerDose, rawDaily, capped, referenceRow,
    give, pattern: give ? dosagePattern(give.patternValue, frequency) : null,
    delivered, needs, coIngredients, notes,
  };
}

// ── Prescription checks (phase 2) ────────────────────────────────────────────


export type TypedDoseCheck = {
  perDose: number;               // base units
  daily: number;
  family: Family;
  perKgPerDose: number | null;
  level: 'ok' | 'warning' | 'error' | 'none';
  summary: string;               // short text for the dosage cell
  notes: Note[];
};

// Amount of ingredient in one ml (liquids) or one tablet/capsule (solids); null if the strength can't tell us
export function amountPerUnit(product: Product, dosageUnit: ParsedDosage['unit']): number | null {
  const s = product.strength;
  if (!s.amount || !unitFamily(s.unit)) return null;
  if (product.kind === 'liquid') return dosageUnit === 'ml' && s.perVolume ? toBase(s.amount, s.unit) / s.perVolume : null;
  if (product.kind === 'solid') return dosageUnit === 'unit' ? toBase(s.amount, s.unit) : null;
  return null;
}

// Reverse check of a dose typed straight into a prescription row ("5ml-5ml-5ml")
export function checkTypedDose(args: {
  parsed: ParsedDosage;
  product: Product;
  weightKg: number;
  ageMonths: number | null;
  moleculeName: string | null;
  rows: PedsDosing[];
}): TypedDoseCheck | null {
  const { parsed, product, weightKg, ageMonths, moleculeName, rows } = args;
  const perUnit = amountPerUnit(product, parsed.unit);
  const family = unitFamily(product.strength.unit);
  if (perUnit == null || !family) return null;
  const perDose = parsed.maxPerDose * perUnit;
  const daily = parsed.perDay * perUnit;
  const hasWeight = weightKg > 0;
  const baseUnit = family === 'mass' ? 'mg' : family;

  const r = computeDose({
    weightKg, ageMonths,
    mode: hasWeight ? 'perKg' : 'fixed',
    rate: hasWeight ? perDose / weightKg : perDose,
    rateUnit: baseUnit, basis: 'dose', frequency: parsed.sos ? 'SOS' : 'QID',
    dosesPerDay: parsed.perDay / parsed.maxPerDose,
    product: null, moleculeName, coIngredients: [], moleculeAutoSelected: false,
    rows, appliedRowId: null, checkOnly: true,
  });
  // Patient-level notes (weight in grams, newborn…) belong to the patient, not each medicine row
  const notes = r.notes.filter(n => !/kg looks like|implausible|Over 40 kg|confirm kg|Newborn|Over 18/.test(n.text));
  const errors = notes.filter(n => n.level === 'error');
  const warnings = notes.filter(n => n.level === 'warning');
  const perKgPerDose = hasWeight ? perDose / weightKg : null;
  const amountLabel = perKgPerDose != null
    ? `${formatNumber(perKgPerDose)} ${baseUnit}/kg`
    : formatAmount(perDose, family);
  const ref = r.referenceRow;
  const status = errors.length ? shortNote(errors[0].text)
    : warnings.length ? shortNote(warnings[0].text)
    : ref && hasWeight ? `✓ ${ref.source}`
    : !hasWeight && rows.some(x => rowMode(x) === 'perKg') ? 'add weight'
    : '';
  return {
    perDose, daily, family, perKgPerDose, notes,
    level: errors.length ? 'error' : warnings.length ? 'warning' : ref ? 'ok' : 'none',
    summary: status ? `${amountLabel} · ${status}` : amountLabel,
  };
}

// First clause of a note, for a narrow cell ("… — may be too weak" → "…")
function shortNote(text: string): string {
  const cut = text.split(' — ')[0]
    .replace(/\s*\([^)]*\)\s*$/, '')
    // The amount is already at the start of the summary: "41.67 mg/kg/dose is 2.8× the IAP max" → "2.8× the IAP max"
    .replace(/^[\d.]+ \S+\/kg\/\w+ is /, '');
  return cut.length > 42 ? cut.slice(0, 40) + '…' : cut;
}

// Highest daily amount the reference allows for this patient (most permissive in-range row), or null if unknown
export function dailyCeiling(rows: PedsDosing[], weightKg: number | null, ageMonths: number | null): number | null {
  const inRange = rows.filter(r => matchRow(r, weightKg, ageMonths).inRange);
  if (!inRange.length) return null;
  const perRow = inRange.map(r => {
    const u = normUnit(r.dose_unit);
    const hi = toBase(Number(r.dose_per_kg_max ?? r.dose_per_kg), u);
    const perDay = r.dose_basis === 'per_dose' ? hi * r.doses_per_day : hi;
    const byWeight = rowMode(r) === 'fixed' ? perDay : weightKg ? perDay * weightKg : null;
    const abs = r.max_daily_dose != null ? Number(r.max_daily_dose) : null;
    const vals = [byWeight, abs].filter((v): v is number => v != null);
    return vals.length ? Math.min(...vals) : null;
  });
  return perRow.some(v => v == null) ? null : Math.max(...(perRow as number[]));
}

export type IngredientLoad = {
  rowIndex: number;
  moleculeId: number;
  name: string;
  daily: number;                 // base units
  family: Family;
  ceiling: number | null;        // from dailyCeiling()
};

// Same ingredient in more than one medicine (e.g. paracetamol in Calpol and a cold syrup): add it up
export function ingredientTotals(loads: IngredientLoad[]): Map<number, Note[]> {
  const byRow = new Map<number, Note[]>();
  const groups = new Map<string, IngredientLoad[]>();
  for (const l of loads) {
    const key = `${l.moleculeId}|${l.family}`;
    groups.set(key, [...(groups.get(key) ?? []), l]);
  }
  for (const group of groups.values()) {
    const rowsInGroup = [...new Set(group.map(l => l.rowIndex))];
    if (rowsInGroup.length < 2) continue;
    const total = group.reduce((a, l) => a + l.daily, 0);
    const ceilings = group.map(l => l.ceiling).filter((c): c is number => c != null);
    const ceiling = ceilings.length ? Math.max(...ceilings) : null;
    const { name, family } = group[0];
    const others = (row: number) => rowsInGroup.filter(r => r !== row).map(r => `#${r + 1}`).join(', ');
    for (const row of rowsInGroup) {
      const over = ceiling != null && total > ceiling * (1 + EPS);
      const text = over
        ? `${name} also in ${others(row)} — ${formatAmount(total, family)}/day total, over the max ${formatAmount(ceiling!, family)}/day`
        : `${name} also in ${others(row)} — ${formatAmount(total, family)}/day total`;
      byRow.set(row, [...(byRow.get(row) ?? []), { level: over ? 'error' : 'warning', text }]);
    }
  }
  return byRow;
}
